import { FC, useState } from "react";
import { Menu, MenuItem, ListItemIcon } from "@mui/material";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { KaerIcon } from "@/icons/kaerIcon";
import CloseIcon from "@mui/icons-material/Close";
import DataSaverOnIcon from "@mui/icons-material/DataSaverOn";
import AddTaskIcon from "@mui/icons-material/AddTask";
import MoreTimeIcon from "@mui/icons-material/MoreTime";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import styles from "@/components/headerbar/headerBarQuickActionsButton.module.css";
import Restricted from "@/consumer/restricted";

interface HeaderBarQuickActionsButtonProps {
  handleRecordTrackedService: () => void;
  handleCreateTodo: () => void;
  handleCreateAppointment: () => void;
  handleUploadDocument: () => void;
  handleAccidentQuickReport: () => void;
  subdomain: string;
}

const HeaderBarQuickActionsButton: FC<HeaderBarQuickActionsButtonProps> = ({
  handleRecordTrackedService,
  handleCreateTodo,
  handleCreateAppointment,
  handleUploadDocument,
  handleAccidentQuickReport,
  subdomain,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (action: () => void) => {
    action();
    handleClose();
  };
  const renderOnAdminOnly = subdomain === "admin";
  const renderOnPortalAndManage =
    subdomain === "portal" || subdomain === "manage";

  return (
    <>
      <GenericButton
        variant="icon"
        color="white"
        onClick={handleClick}
        aria-controls={open ? "trackedServiceMenu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
      >
        <KaerIcon icon="addEntry" color="#FFF" />
      </GenericButton>
      <Menu
        id="trackedServiceMenu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              overflow: "visible",
              filter: isMobile
                ? "drop-shadow(0px 10px 20px rgba(0,0,0,0.9))"
                : "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
              mt: isMobile ? 2.0 : 1.5,
              pt: isMobile ? 2 : undefined,
              width: isMobile ? "100%" : undefined,
              "& .MuiAvatar-root": {
                width: 36,
                height: 36,
                ml: -0.5,
                mr: 1,
              },
              "&:before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: isMobile ? 62 : 18,
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {isMobile && (
          <GenericButton
            variant="icon"
            color="iconBase"
            onClick={handleClose}
            className={styles.closeButton}
          >
            <CloseIcon />
          </GenericButton>
        )}

        {renderOnAdminOnly && (
          <div>
            <Restricted permissionRequired="frontend_view_trackedservice_headerbar">
              <MenuItem
                onClick={() => handleMenuItemClick(handleRecordTrackedService)}
              >
                <ListItemIcon>
                  <DataSaverOnIcon />
                </ListItemIcon>
                {t("trackedservice_create_short")}
              </MenuItem>
            </Restricted>

            <Restricted permissionRequired="frontend_view_todo_creator">
              <MenuItem onClick={() => handleMenuItemClick(handleCreateTodo)}>
                <ListItemIcon>
                  <AddTaskIcon />
                </ListItemIcon>
                {t("create_todo")}
              </MenuItem>
            </Restricted>

            <Restricted permissionRequired="frontend_view_appointment_dashboard">
              <MenuItem
                onClick={() => handleMenuItemClick(handleCreateAppointment)}
              >
                <ListItemIcon>
                  <MoreTimeIcon />
                </ListItemIcon>
                {t("create_appointment")}
              </MenuItem>
            </Restricted>
            <Restricted permissionRequired="frontend_view_admin_documents">
              <MenuItem
                onClick={() => handleMenuItemClick(handleUploadDocument)}
              >
                <ListItemIcon>
                  <FileUploadIcon />
                </ListItemIcon>
                {t("upload_file")}
              </MenuItem>
            </Restricted>
          </div>
        )}

        {renderOnPortalAndManage && (
          <Restricted permissionRequired="frontend_view_quickaction_accident_report">
            <MenuItem
              onClick={() => handleMenuItemClick(handleAccidentQuickReport)}
            >
              <ListItemIcon>
                <KaerIcon icon="accidentbook" />
              </ListItemIcon>
              {t("record_accident_report")}
            </MenuItem>
          </Restricted>
        )}
      </Menu>
    </>
  );
};

export default HeaderBarQuickActionsButton;
